import { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API } from "@apis/index";
import { buildSbomReadableHtml } from "./sbomReadableHtml";
import { resolveSbomReadableViewRequest } from "@utils/sbomReadableView";

const SbomReadableViewPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");

  const request = useMemo(
    () => resolveSbomReadableViewRequest(Object.fromEntries(searchParams.entries())),
    [searchParams],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!request?.endpoint) return;

      try {
        const { data } = await API.get(`/${request.endpoint}`, {
          params: request.params,
        });
        if (cancelled) return;

        const view = {
          ...(data || {}),
          metadata: {
            ...(data?.metadata || {}),
            ...(request.title ? { title: request.title } : {}),
          },
        };
        const html = buildSbomReadableHtml(view);
        document.open();
        document.write(html);
        document.close();
      } catch (error) {
        if (cancelled) return;
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.additionalMessage ||
          t(
            "project.verification-report.readable-view-error",
            "Failed to load SBOM readable view.",
          );
        setErrorMessage(message);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [request, t]);

  if (!request?.endpoint) {
    return <Navigate to="/404" replace />;
  }

  if (errorMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-8">
        <p className="text-sm text-red-600">{errorMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-8">
      <p className="text-sm text-slate-600">
        {t("common.page-loading", "Loading...")}
      </p>
    </div>
  );
};

export default SbomReadableViewPage;
